/**
 * Sinais do diagnóstico de instalação (`/instalacao`), coletados pelo widget.
 *
 * O backend agrega por company, mês e página (`guide_install_signal`). Para que isso custe quase
 * nada no banco, o widget só manda o sinal UMA vez por navegador, por página e por dia — o resto
 * dos carregamentos segue com o identify de sempre, sem o campo `installSignal`.
 */

const STORAGE_KEY = 'getdemo_install_signal';
/** Teto de chaves lembradas por dia; acima disso o navegador simplesmente reenvia. */
const MAX_KEYS = 200;

/**
 * Página com campo de senha VISÍVEL. É o sinal mais barato e mais confiável de tela de login, e
 * não depende de a URL conter "login": funciona em `/`, `/entrar`, `/auth` ou qualquer outro
 * caminho. Campo escondido (modal fechado, formulário de troca de senha colapsado) não conta.
 */
export function hasVisiblePasswordField(doc: Document = document): boolean {
  try {
    const fields = doc.querySelectorAll<HTMLInputElement>('input[type="password"]');
    return Array.from(fields).some(field => field.getClientRects().length > 0);
  } catch {
    return false;
  }
}

export interface InstallSignalInput {
  companyId: string;
  pageUrl: string | undefined;
  identified: boolean;
  lateIdentify: boolean;
  now?: Date;
  storage?: Storage;
}

function pageKey(pageUrl: string): string | undefined {
  try {
    const { origin, pathname } = new URL(pageUrl);
    return `${origin}${pathname}`;
  } catch {
    return undefined;
  }
}

/**
 * Diz se este identify deve levar o sinal e, se sim, já o marca como enviado. Identify tardio é
 * uma chave à parte: a mesma página pode ter mandado o sinal anônimo mais cedo no mesmo dia, e o
 * tardio é justamente o que o diagnóstico precisa ver.
 */
export function shouldSendInstallSignal({
  companyId,
  pageUrl,
  identified,
  lateIdentify,
  now = new Date(),
  storage
}: InstallSignalInput): boolean {
  const page = pageUrl ? pageKey(pageUrl) : undefined;
  if (!companyId || !page) return false;

  const day = now.toISOString().slice(0, 10);
  const key = `${companyId}|${page}|${identified ? 'i' : 'a'}${lateIdentify ? 'l' : ''}`;

  try {
    const store = storage ?? window.localStorage;
    const saved = JSON.parse(store.getItem(STORAGE_KEY) || 'null') as {
      day: string;
      keys: string[];
    } | null;
    const keys = saved?.day === day ? saved.keys : [];
    if (keys.includes(key)) return false;

    const next = [...keys, key].slice(-MAX_KEYS);
    store.setItem(STORAGE_KEY, JSON.stringify({ day, keys: next }));
    return true;
  } catch {
    // Storage bloqueado (modo privado, iframe sem permissão): sem como deduplicar, não envia.
    return false;
  }
}
